import React, { useState } from "react";
import {
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Grid,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { showSnackbar } from "../../../redux/snackbar/actions";
import { useFormik } from "formik";
import * as yup from "yup";
import authApi from "../../../services/auth";
import { encryptAES } from "../../../utils";

const useStyles = makeStyles({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },
  },
});

const ChangePassword = () => {
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems?.find(
    (item) => item.name.toLowerCase() === "business settings"
  );
  const isDisabled = createMenuItem?.access_type === "read_write";
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpField, setShowOtpField] = useState(false);

  const handleClickShowOldPassword = () => setShowOldPassword(!showOldPassword);
  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleVerifyEmail = () => {
    setShowOtpField(true);
  };

  const handleVerifyOtp = () => {
    console.log("OTP verified:", otp);
  };

  const handleSubmit = (formData) => {
    const data = {
      password: formData?.password,
      newPassword: formData?.npassword,
    };
    const encryptedData = encryptAES(data);
    dispatch(showLoader());
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Info updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Invalid Credentials",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      formik.resetForm();
    };
    authApi.changePassword(encryptedData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      npassword: "",
      cpassword: "",
    },
    validationSchema: yup.object({
      password: yup
        .string()
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/,
          "Password must be 8-12 characters, include at least one letter, one number, and one special character"
        )
        .required("Password is required"),
      npassword: yup
        .string()
        .required("New password is required")
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/,
          "Password must be 8-12 characters, include at least one letter, one number, and one special character"
        ),
      cpassword: yup
        .string()
        .oneOf([yup.ref("npassword"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: handleSubmit,
  });

  return (
    <>
      <Grid container spacing={2}>
        {showOtpField && (
          <>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                fullWidth
                label="Enter OTP"
                autoComplete="off"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Button
                variant="contained"
                onClick={handleVerifyOtp}
                className={classes.animatedButton}
              >
                Verify OTP
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      <Stack style={{ marginTop: "30px" }}>
        <Typography variant="subtitle1">Change Password</Typography>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={4}>
          <TextField
            fullWidth
            label="Old Password"
            type={showOldPassword ? "text" : "password"}
            autoComplete="off"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowOldPassword} edge="end">
                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            autoComplete="off"
            name="npassword"
            value={formik.values.npassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.npassword && Boolean(formik.errors.npassword)}
            helperText={formik.touched.npassword && formik.errors.npassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowNewPassword} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            autoComplete="off"
            name="cpassword"
            value={formik.values.cpassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.cpassword && Boolean(formik.errors.cpassword)}
            helperText={formik.touched.cpassword && formik.errors.cpassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowConfirmPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {isDisabled && (
          <Grid item xs={12}>
            <Grid container justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                className={classes.animatedButton}
                onClick={formik.handleSubmit}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default ChangePassword;
